import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import "../sideMenu/style.scss";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { useTranslation } from "react-i18next";
import { useCurrentDesignComponentLists, useCurrentDesignVersion, useDesignVersionStatusAndComponents, useTabAccessWithFallback } from "../../constants/helper";
import SEConfirmModal from "../common/SEConfirmModal";
import { navigateToComponentAtom } from "../../utils/atoms/navigateToComponentAtom";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
const ManageProgressBar = () => {
  const [activeState, setActiveState] = useAtom(activeTabAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [componentStatus] = useAtom(componentStatusAtom);
  const [projectComponents] = useAtom(componentsAtom);
  const { t } = useTranslation();

  useEffect(() => {
    const handleTabChange = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab")?.toUpperCase();
      if (["DESIGN", "SIMULATION", "RESULT"].includes(tab)) {
        setActiveState(tab);
      }
    };

    // Run on mount
    handleTabChange();

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", handleTabChange);
    return () => window.removeEventListener("popstate", handleTabChange);
  }, [setActiveState]);
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList);
//  const {
//     modal: fallbackModal
//   }   = useTabAccessWithFallback(projectDetails?.designWithVersionsDTOList, projectDetails?.designComponentsList)
  const canViewTabs = useDesignVersionStatusAndComponents(projectDetails?.designWithVersionsDTOList, projectDetails?.designComponentsList)
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);
  // Access modal state for blocked tab navigation
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessModalMessage, setAccessModalMessage] = useState("");
  const [, setNavigateToComponent] = useAtom(navigateToComponentAtom);
  // Thermal/genset Validation 1 modal (thermal selected but no genset configured at all)
  const [thermalNoGensetModal, setThermalNoGensetModal] = useState(false);
  // Thermal/genset Validation 3 modal (thermal selected but ControlledGenset configured)
  const [thermalControlledModal, setThermalControlledModal] = useState(false);
  // Pending navigation target when user clicks a tab and we need to confirm first
  const [pendingTab, setPendingTab] = useState(null);

  return (
    <se-tabbar
      color="alternative"
      style={{
        zIndex: "10",
        boxShadow: "rgba(90, 116, 148, 0.3) 0px 4px 4px",
        marginBottom: activeState === "DESIGN" ? "4px" : "0",
      }}
    >
      {/* {fallbackModal} */}
      <nav className="row mx-0" style={{ zIndex: "10" }}>
        {["DESIGN", "SIMULATION", "RESULT"].map((activeTab) => (
          <a
            className={`col-sm-4 ${activeState === activeTab ? "active" : ""}`}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (activeState === "DESIGN" && activeState !== activeTab) {
                const editingComponent = Object.entries(componentStatus || {}).find(
                  ([, status]) =>
                    status &&
                    typeof status === "object" &&
                    status?.isEditing &&
                    !status?.completed
                );
                if (editingComponent) {
                  const componentKey = editingComponent?.[0] || t("common.selected");
                  const displayComponent = componentKey === "battery" ? "BESS" : componentKey === "controller" ? "Controls" : componentKey;
                  setAccessModalMessage(
                    t("manageProgress.unsavedEdit.message", {
                      component: displayComponent,
                    })
                  );
                  setAccessModalOpen(true);
                  return;
                }
              }

              // Simulation access check: allow click but block navigation and show error when prerequisites missing
              if (activeTab === "SIMULATION" && !canViewTabs?.canViewSimulation) {
                setAccessModalMessage('Design should have Controls and Grid to proceed Simulation');
                setAccessModalOpen(true);
                return;
              }
              // Validation 1: Thermal selected but no genset component configured at all
              // Validation 3: Thermal selected but ControlledGenset configured
              if (activeTab === "SIMULATION") {
                const hasThermalLoad = canViewTabs?.hasThermalLoad;
                if (hasThermalLoad) {
                  const hasGensetComponent = selectedDesignAndDesignVersion.componentDTOList.some(
                    (c) => c.componentType?.toLowerCase() === "baseload genset"
                  );
                  if (!hasGensetComponent) {
                    // V1: no genset configured at all
                    setPendingTab(activeTab);
                    setThermalNoGensetModal(true);
                    return;
                  }
                  // Genset exists — check if it's Controlled (V3)
                  const gensetType = projectComponents?.genset?.componentAttributes?.gensetParameters?.GensetType;
                  if (gensetType === "ControlledGenset") {
                    setPendingTab(activeTab);
                    setThermalControlledModal(true);
                    return;
                  }
                }
              }
              if (activeTab === "RESULT" && canViewTabs?.versionStatus !== "COMPLETED") {
                setAccessModalMessage('Please complete the simulation (Design Version Status must be set to COMPLETED) to access the Results.');
                setAccessModalOpen(true);
                return;
              }
              setActiveState(activeTab);
              const url = new URL(window.location);
              url.searchParams.set("tab", activeTab.toLowerCase());
              window.history.replaceState({}, "", url);
            }}
            key={activeTab}
            style={{
              justifyContent: "center",
              zIndex: "10",
              cursor: (activeTab === "SIMULATION" && !canViewTabs?.canViewSimulation) || (activeTab === "RESULT" && canViewTabs?.versionStatus !== "COMPLETED") ? 'not-allowed' : 'pointer',
              opacity: (activeTab === "RESULT" && canViewTabs?.versionStatus !== "COMPLETED") ? 0.55 : 1,
              borderBottom:
                activeState !== activeTab
                  ? "2px solid rgba(0, 0, 0, 0.1)"
                  : "none",
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          >
            <span>{activeTab}</span>
          </a>
        ))}
      </nav>
      <nav slot="none"></nav>
      <SEConfirmModal
        opened={accessModalOpen}
        onClose={() => { setAccessModalOpen(false); setAccessModalMessage(""); }}
        onConfirm={() => { setAccessModalOpen(false); setAccessModalMessage(""); }}
        confirmLabel={t("common.ok")}
        showCancel={false}
        header={"Warning"}
        message={<div style={{ lineHeight: 1.4 }}>{accessModalMessage}</div>}
      />
      {/* Validation 1: Thermal Load selected but no Genset configured */}
      <SEConfirmModal
        opened={thermalNoGensetModal}
        onClose={() => {
          // "Continue" → proceed to simulation tab without thermal in payload
          setThermalNoGensetModal(false);
          if (pendingTab) {
            setActiveState(pendingTab);
            const url = new URL(window.location);
            url.searchParams.set("tab", pendingTab.toLowerCase());
            window.history.replaceState({}, "", url);
            setPendingTab(null);
          }
        }}
        onConfirm={() => {
          // "Yes" → stay on DESIGN tab and navigate to Genset panel
          setThermalNoGensetModal(false);
          setPendingTab(null);
          setNavigateToComponent("genset");
        }}
        header={t("genset.thermalValidation.header")}
        message={
          <div>
            <p style={{ marginBottom: "0.75rem" }}>
              {t("genset.thermalValidation.noGensetWithThermal")}
            </p>
            <div style={{ border: "1px solid white", padding: "0.6rem 0.75rem", borderRadius: "4px" }}>
              <strong>{t("common.note")}</strong>
              <ul style={{ margin: "0.4rem 0 0 0", paddingLeft: "1.2rem" }}>
                <li><strong>{t("common.continue")}</strong> → {t("genset.thermalValidation.note.noGenset.continue")}</li>
                <li><strong>{t("common.yes")}</strong> → {t("genset.thermalValidation.note.noGenset.yes")}</li>
              </ul>
            </div>
          </div>
        }
        confirmLabel={t("common.yes")}
        cancelLabel={t("common.continue")}
        showCancel
      />
      {/* Validation 3: Thermal Load selected but ControlledGenset configured */}
      <SEConfirmModal
        opened={thermalControlledModal}
        onClose={() => {
          // "Continue" → proceed to simulation tab (thermal excluded from payload)
          setThermalControlledModal(false);
          if (pendingTab) {
            setActiveState(pendingTab);
            const url = new URL(window.location);
            url.searchParams.set("tab", pendingTab.toLowerCase());
            window.history.replaceState({}, "", url);
            setPendingTab(null);
          }
        }}
        onConfirm={() => {
          // "Yes" → stay on DESIGN tab and go to Genset panel
          setThermalControlledModal(false);
          setPendingTab(null);
          setNavigateToComponent("genset");
        }}
        header={t("genset.thermalValidation.header")}
        message={
          <div>
            <p style={{ marginBottom: "0.75rem" }}>
              {t("genset.thermalValidation.controlledWithThermal")}
            </p>
            <div style={{ border: "1px solid white", padding: "0.6rem 0.75rem", borderRadius: "4px" }}>
              <strong>{t("common.note")}</strong>
              <ul style={{ margin: "0.4rem 0 0 0", paddingLeft: "1.2rem" }}>
                <li><strong>{t("common.continue")}</strong> → {t("genset.thermalValidation.note.controlled.continue")}</li>
                <li><strong>{t("common.yes")}</strong> → {t("genset.thermalValidation.note.controlled.yes")}</li>
              </ul>
            </div>
          </div>
        }
        confirmLabel={t("common.yes")}
        cancelLabel={t("common.continue")}
        showCancel
      />
    </se-tabbar>
  );
};
export default ManageProgressBar;
